import { CategoryData, GlossaryEntry, ScoreStatistics } from '@/lib/api';
import { SubcategoryRow } from './SubcategoryRow';
import { EMPTY_SCORE } from './utils';
import { useTranslations } from 'next-intl';
import { ScoreComparison } from '../ScoreComparison';

interface CategorySectionProps {
    category: CategoryData;
    cluster: string;
    isExpanded: boolean;
    toggleCategory: (name: string) => void;
    categoryScore: number | null;
    glossaryDefinition: GlossaryEntry | null;
    scores: Map<string, { score: number | null; reliability: number; raw_value: string | null }>;
    getScoreKey: (cat: string, sub: string) => string;
    onScoreChange: (cat: string, sub: string, newScore: number | null | undefined, newRel?: number, newRaw?: string | null) => void;
    getScoreColor: (score: number | null) => string;
    getGlossaryDefinition: (name: string) => GlossaryEntry | null;
    getCategoryStats?: (cluster: string, categoryName: string) => ScoreStatistics | null;
    getSubcategoryStats?: (cluster: string, categoryName: string, subcategoryName: string) => ScoreStatistics | null;
    getCategoryWeight?: (categoryName: string) => number;
    getSubcategoryWeight?: (categoryName: string, subcategoryName: string) => number;
    isReadOnly?: boolean;
}

export function CategorySection({
    category,
    cluster,
    isExpanded,
    toggleCategory,
    categoryScore,
    glossaryDefinition,
    scores,
    getScoreKey,
    onScoreChange,
    getScoreColor,
    getGlossaryDefinition,
    getCategoryStats,
    getSubcategoryStats,
    getCategoryWeight,
    getSubcategoryWeight,
    isReadOnly = false
}: CategorySectionProps) {
    const t = useTranslations('certification.stage2');

    const categoryStats = getCategoryStats ? getCategoryStats(cluster, category.category) : null;
    const categoryWeight = getCategoryWeight ? getCategoryWeight(category.category) : 1;
    const isCategoryDisabled = categoryWeight === 0;

    return (
        <div className={`border border-slate-200 bg-transparent ${isCategoryDisabled ? 'opacity-85 bg-slate-50' : ''}`}>
            {/* Category Header */}
            <button
                onClick={() => toggleCategory(category.category)}
                className="w-full flex items-center justify-between p-4 bg-transparent hover:bg-slate-50/50 transition-colors cursor-pointer"
                type="button"
            >
                <div className="flex items-center gap-3">
                    <svg
                        className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''} ${isCategoryDisabled ? 'text-slate-300' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className={`font-medium ${isCategoryDisabled ? 'text-slate-400' : 'text-black'}`}>
                        {category.category}
                        {isCategoryDisabled && (
                            <span className="ml-2 text-[10px] uppercase font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 border border-slate-200">
                                {t('noWeight')}
                            </span>
                        )}
                    </span>
                    {/* Show glossary even if disabled, but prevent click propagation */}
                    {glossaryDefinition && (
                        <div className="group relative z-20 hover:z-50" onClick={(e) => e.stopPropagation()}>
                            <svg className="w-4 h-4 text-slate-400 hover:text-violet-500 transition-colors cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="absolute left-0 top-6 z-[9999] hidden group-hover:block w-80 p-3 bg-slate-800 text-white text-xs shadow-xl border border-slate-700 pointer-events-none text-left whitespace-normal break-words">
                                <div className="font-semibold mb-1">{glossaryDefinition.terminology}</div>
                                <div>{glossaryDefinition.definition}</div>
                            </div>
                        </div>
                    )}
                    {/* Score Comparison for Category */}
                    {!isCategoryDisabled && categoryStats && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <ScoreComparison userScore={categoryScore} stats={categoryStats} compact />
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {!isCategoryDisabled && categoryScore !== null && (
                        <span className={`px-2 py-1 text-sm font-semibold ${getScoreColor(categoryScore)} text-white`}>
                            {categoryScore.toFixed(1)}
                        </span>
                    )}

                </div>
            </button>

            {/* Subcategories */}
            {isExpanded && (
                <div className="divide-y divide-slate-100 overflow-visible">
                    {category.subcategories.map((sub, index) => {
                        const key = getScoreKey(category.category, sub.name);
                        const currentScore = scores.get(key) || EMPTY_SCORE;
                        const subDefinition = sub.indicator
                            ? { cluster: '', terminology: sub.name, definition: sub.indicator }
                            : getGlossaryDefinition(sub.name);
                        const subStats = getSubcategoryStats ? getSubcategoryStats(cluster, category.category, sub.name) : null;
                        const subWeight = getSubcategoryWeight ? getSubcategoryWeight(category.category, sub.name) : 1;

                        return (
                            <SubcategoryRow
                                key={sub.name}
                                categoryName={category.category}
                                sub={sub}
                                currentScore={currentScore}
                                subDefinition={subDefinition}
                                t={t}
                                getScoreColor={getScoreColor}
                                onScoreChange={onScoreChange}
                                stats={subStats}
                                weight={subWeight}
                                isReadOnly={isReadOnly}
                                isFirst={index === 0}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}
